/**
 * VMS vs IMS brand assets and document meta (logos live under /public/branding).
 */

export type BrandId = 'vms' | 'ims' | 'neutral';

export const BRAND = {
  vms: {
    logoLight: '/branding/vms-logo-light.svg',
    logoDark: '/branding/vms-logo-dark.svg',
    favicon16: '/branding/vms-favicon-16.svg',
    favicon32: '/branding/vms-favicon-32.svg',
    icon72: '/branding/vms-icon-72.svg',
  },
  ims: {
    logoLight: '/branding/ims-logo-light.svg',
    logoDark: '/branding/ims-logo-dark.svg',
    favicon16: '/branding/ims-favicon-16.svg',
    favicon32: '/branding/ims-favicon-32.svg',
    icon72: '/branding/ims-icon-72.svg',
  },
} as const;

/** Query ?system=vms | ?system=ims (e.g. login deep-link). */
export function resolveBrandId(pathname: string, search: string): BrandId {
  const q = new URLSearchParams(search);
  const system = q.get('system');
  if (system === 'vms' || system === 'ims') return system;
  if (
    pathname === '/login' ||
    pathname === '/forgot-password' ||
    pathname === '/forget-password' ||
    pathname.startsWith('/forget-password/')
  ) {
    return 'vms';
  }
  if (pathname === '/vms' || pathname.startsWith('/vms/')) return 'vms';
  if (pathname === '/ims' || pathname.startsWith('/ims/')) return 'ims';
  if (pathname === '/system-selection') return 'neutral';
  return 'vms';
}

/** For UI (logos): neutral pages fall back to IMS for primary logo unless you show both. */
export function resolveBrandForUi(pathname: string, search: string): 'vms' | 'ims' {
  const id = resolveBrandId(pathname, search);
  if (id === 'neutral') return 'ims';
  return id;
}

export interface BrandDocumentMeta {
  title: string;
  description: string;
  applicationName: string;
  themeColor: string;
  favicon16: string;
  favicon32: string;
  icon72: string;
  ogSiteName: string;
  ogTitle: string;
  ogDescription: string;
  twitterTitle: string;
  twitterDescription: string;
}

/** Matches Tailwind `primary-600` */
const THEME = '#a11035';

export function getBrandDocumentMeta(brandId: BrandId): BrandDocumentMeta {
  if (brandId === 'vms') {
    return {
      title: 'VMS — Vendor Management',
      description:
        'Vendor Management System (VMS): vendor lifecycle, RFP, onboarding, compliance, and stakeholder workflows.',
      applicationName: 'VMS',
      themeColor: THEME,
      favicon16: BRAND.vms.favicon16,
      favicon32: BRAND.vms.favicon32,
      icon72: BRAND.vms.icon72,
      ogSiteName: 'VMS',
      ogTitle: 'VMS — Vendor Management',
      ogDescription:
        'Enterprise Vendor Management: vendor lifecycle, RFP, onboarding, and compliance.',
      twitterTitle: 'VMS — Vendor Management',
      twitterDescription: 'Vendor Management System for enterprise procurement and onboarding.',
    };
  }
  if (brandId === 'ims') {
    return {
      title: 'IMS — Invoice Management',
      description:
        'Invoice Management System (IMS): invoices, approvals, vendors, and financial workflows.',
      applicationName: 'IMS',
      themeColor: THEME,
      favicon16: BRAND.ims.favicon16,
      favicon32: BRAND.ims.favicon32,
      icon72: BRAND.ims.icon72,
      ogSiteName: 'IMS',
      ogTitle: 'IMS — Invoice Management',
      ogDescription: 'Invoice Management: processing, approvals, and financial controls.',
      twitterTitle: 'IMS — Invoice Management',
      twitterDescription: 'Invoice Management System for enterprise finance teams.',
    };
  }
  return {
    title: 'VMS — Vendor Management',
    description:
      'Vendor Management System (VMS): vendor lifecycle, RFP, onboarding, compliance, and stakeholder workflows.',
    applicationName: 'VMS',
    themeColor: THEME,
    favicon16: BRAND.vms.favicon16,
    favicon32: BRAND.vms.favicon32,
    icon72: BRAND.vms.icon72,
    ogSiteName: 'VMS',
    ogTitle: 'VMS — Vendor Management',
    ogDescription: 'Enterprise Vendor Management: vendor lifecycle, RFP, onboarding, and compliance.',
    twitterTitle: 'VMS — Vendor Management',
    twitterDescription: 'Vendor Management System for enterprise procurement and onboarding.',
  };
}
